import React, { useEffect, useState } from 'react';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { format } from 'date-fns';
import styles from './ScheduleTab.module.css';
import { GuardTabs } from 'pages/CANguard/CANguard';
import {
	Box,
	Chip,
	CircularProgress,
	IconButton,
	Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachmentCard from '../../attachmentCard/AttachmentCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import useFetchDDSFiles from 'containers/pages/CANguard/hooks/ddsUploads/useFetchDDSFiles';
import useDeleteDDSFile from 'containers/pages/CANguard/hooks/ddsUploads/useDeleteDDSFile';
import useUploadDDSFile from 'containers/pages/CANguard/hooks/ddsUploads/useUploadDDSFile';
import { useGuardRoleContext } from 'containers/pages/CANguard/context/GuardRoleProvider';
import checkOngoingUploads from 'containers/pages/CANguard/utils/checkOngoingUpload';
import { useScheduleContext } from 'containers/pages/CANguard/context/ScheduleProvider';

const DDSUploadTableCell: React.FC<{
	patrol: ScheduleAssignment;
	ddsFiles?: PatrolAttachment[];
	ddsFilesLength: number;
	expanded: boolean;
	isUploading: boolean;
	setExpanded: (expanded: boolean) => void;
	handleUploadButtonClick: (row: ScheduleAssignment) => void;
}> = ({
	patrol,
	ddsFiles,
	ddsFilesLength,
	expanded,
	isUploading,
	setExpanded,
	handleUploadButtonClick,
}) => {
	return (
		<TableCell align="left">
			<Box className={styles.upload}>
				<Chip
					label={`${
						ddsFiles ? ddsFiles.length.toString() : ddsFilesLength // if ddsFiles is undefined, use ddsFilesLength. After fetching & uploads, ddsFiles will be set and have the correct info
					} uploaded`}
					icon={!expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
					onClick={(e: any) => {
						e.stopPropagation();
						setExpanded(!expanded);
					}}
				/>
				<IconButton
					onClick={(e: any) => {
						e.stopPropagation();
						handleUploadButtonClick(patrol);
					}}
				>
					<CloudUploadIcon />
				</IconButton>
				{isUploading && <CircularProgress size={24} />}
			</Box>
		</TableCell>
	);
};

const ScheduleRow: React.FC<{
	row: ScheduleAssignment;
	index: number;
	goToTab: (tab: GuardTabs) => void;
}> = ({ row, index, goToTab }) => {
	const [expanded, setExpanded] = useState(false);
	const { isAdmin } = useGuardRoleContext();
	const { goToPatrol } = useScheduleContext();
	const { loadingFiles, fetchDDSFiles } = useFetchDDSFiles(row);
	const handleDeleteDDSFile = useDeleteDDSFile(row);
	const { handleUploadButtonClick, uppyInstance } = useUploadDDSFile(row);

	useEffect(() => {
		if (expanded && !row.dds_files && row.comment_list_id) {
			fetchDDSFiles();
		}
	}, [expanded, fetchDDSFiles, row.dds_files]);

	return (
		<>
			<TableRow
				sx={{
					'&:last-child td, &:last-child th': {
						border: 0,
					},
					cursor: 'pointer',
				}}
				onClick={() => {
					goToTab(GuardTabs.Patrols);
					goToPatrol(index);
				}}
			>
				<TableCell align="left">{index + 1}.</TableCell>
				<TableCell align="left">
					{row.comment_list_id || 'N/A'}
				</TableCell>
				{isAdmin && (
					<DDSUploadTableCell
						patrol={row}
						ddsFiles={row.dds_files}
						ddsFilesLength={row.dds_file_id.length}
						expanded={expanded}
						setExpanded={setExpanded}
						handleUploadButtonClick={handleUploadButtonClick}
						isUploading={checkOngoingUploads(uppyInstance)}
					/>
				)}

				<TableCell align="left" className={styles.customer}>
					{row.cust_name}
				</TableCell>
				<TableCell align="left" className={styles.address}>
					{row.address}
				</TableCell>
				<TableCell align="left">
					<span className={styles.date}>
						{format(row.start_time, 'MMM d,')}
					</span>{' '}
					<span className={styles.time}>
						{format(row.start_time, 'p')}
						{' - '}
						{format(row.end_time, 'p')}
					</span>
				</TableCell>

				<TableCell
					align="left"
					className={
						row.complete ? styles.completed : styles.inProgress
					}
				>
					{row.complete ? 'Complete' : 'Not Complete'}
				</TableCell>
			</TableRow>
			{expanded && (
				<TableRow>
					<TableCell /> {/* Empty cell to apply indent...*/}
					<TableCell colSpan={6} sx={{ p: 0 }}>
						<Box
							sx={{
								bgcolor: 'background.default',
								display: 'flex',
								flexDirection: 'column',
								p: 2,
							}}
						>
							{loadingFiles ? (
								<Typography>Loading...</Typography>
							) : (
								<>
									<Typography variant="h6">
										DDS Attachments
									</Typography>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: 2,
										}}
									>
										{row.dds_files?.map(
											(attachment, index) => (
												<AttachmentCard
													id={attachment.AttachmentID}
													name={attachment.Name}
													contentType={
														attachment.ContentType
													}
													size={attachment.Size}
													index={index}
													onConfirmDelete={(id) =>
														handleDeleteDDSFile(id)
													}
													isDDSFile={true}
												/>
											),
										)}
									</Box>
								</>
							)}
						</Box>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};

export default ScheduleRow;
